import type { DefaultTheme } from 'styled-components'
import styled, { css } from 'styled-components'

import { unreachable } from '@helpers'

import type { TypographyProps } from '../Typography'
import { Typography } from '../Typography'
import { INFORMATIONAL_MESSAGE_VARIANTS } from './constant'

export type InformationalMessageVariant = ConstObjectValues<typeof INFORMATIONAL_MESSAGE_VARIANTS>

function getVariantStyles(theme: DefaultTheme, variant: InformationalMessageVariant) {
  switch (variant) {
    case INFORMATIONAL_MESSAGE_VARIANTS.error:
      return css`
        background-color: #ffeceb;
        border-color: #f2b5b8;
        color: #353333;
      `
    case INFORMATIONAL_MESSAGE_VARIANTS.info:
      return css`
        background-color: ${theme.colors.common.demoBlue};
        border-color: ${theme.colors.common.demoBlue};
        color: ${theme.colors.gray[100]};
        & > svg {
          color: ${theme.colors.blue[40]};
        }
      `
    case INFORMATIONAL_MESSAGE_VARIANTS.warning:
      return css`
        background-color: #fffad8;
        border-color: #f3c63a;
        color: #353333;
      `

    case INFORMATIONAL_MESSAGE_VARIANTS.default:
      return css`
        background-color: ${theme.colors.gray[2]};
        border-color: ${theme.colors.gray[2]};
        color: ${theme.colors.gray[70]};
      `
    default:
      return unreachable(variant)
  }
}

const VARIANT_SIZES: {
  [key in InformationalMessageVariant]: TypographyProps['size']
} = {
  [INFORMATIONAL_MESSAGE_VARIANTS.error]: '400-xs',
  [INFORMATIONAL_MESSAGE_VARIANTS.info]: '400-sm',
  [INFORMATIONAL_MESSAGE_VARIANTS.warning]: '400-sm',
  [INFORMATIONAL_MESSAGE_VARIANTS.default]: '400-sm',
}

const InformationalMessageDiv = styled.div.withConfig<{
  variant: InformationalMessageVariant
}>({
  shouldForwardProp: (prop, defaultValidator) => {
    return prop !== 'variant' && defaultValidator(prop)
  },
})`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 570px;
  border-radius: 8px;
  border: 1px solid;
  padding: 8px 12px;

  ${({ theme, variant }) => getVariantStyles(theme, variant)}
`
interface InformationalMessageProps {
  children: StringOrMessage
  className?: string
  typographySize?: TypographyProps['size']
  variant?: InformationalMessageVariant
}

export function InformationalMessage({
  children,
  className,
  typographySize,
  variant = INFORMATIONAL_MESSAGE_VARIANTS.default,
}: InformationalMessageProps) {
  return (
    <InformationalMessageDiv variant={variant} className={className}>
      <Typography size={typographySize ?? VARIANT_SIZES[variant]}>{children}</Typography>
    </InformationalMessageDiv>
  )
}
